import { useState } from 'react'
import type { JSX, SyntheticEvent } from 'react'
import type { ApiFailure, ApiResult } from '@/api/client'
import { Button } from '@/ui/Button'
import { Field } from '@/ui/Field'
import { InlineError } from '@/ui/states'
import { TIMES } from './times'

/** The send window as a form holds it: `HH:MM`, never the API's `HH:MM:SS`. */
export interface SendWindowValue {
  window_start: string
  window_end: string
  skip_weekends: boolean
}

/**
 * The send window, as one form used by the Settings panel and the wizard step.
 *
 * Settings consolidation (2026-09-15): moved from `pages/outreach/SendWindowPanel`,
 * which saved on every change with no Save button (P-HLTH-07). That was the
 * form that never closed; here Save is explicit and the caller decides what a
 * successful submit does next — collapse the row, or move the wizard on.
 *
 * The form owns the failure: a refused save is shown here, beside the fields
 * that caused it, and the form stays open until the person fixes or cancels.
 */
export function SendWindowForm({
  value,
  onSubmit,
  submitLabel,
  disabled = false,
}: {
  value: SendWindowValue
  onSubmit: (value: SendWindowValue) => Promise<ApiResult<unknown>>
  submitLabel: string
  disabled?: boolean
}): JSX.Element {
  const [draft, setDraft] = useState<SendWindowValue>(value)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [busy, setBusy] = useState(false)
  // The server refuses this with a 422 and says so; the button is disabled first
  // so the tenant finds out while they are still looking at the two fields that
  // caused it, rather than after a round trip.
  const ordered = draft.window_start < draft.window_end

  function edit(changes: Partial<SendWindowValue>): void {
    setFailure(null)
    setDraft((current) => ({ ...current, ...changes }))
  }

  async function submit(event: SyntheticEvent): Promise<void> {
    event.preventDefault()
    if (!ordered) return
    setBusy(true)
    const result = await onSubmit(draft)
    setBusy(false)
    if (!result.ok) setFailure(result.failure)
  }

  return (
    <form className="ar-stack" aria-label="Send window" onSubmit={(event) => void submit(event)}>
      <Field
        label="Only act between"
        help="IST. Activity outside business hours is a strong ban signal."
        error={ordered ? undefined : 'The start has to be before the end.'}
      >
        {(aria) => (
          <div className="ar-row">
            <select
              className="ar-select"
              style={{ flex: 1 }}
              {...aria}
              disabled={disabled}
              value={draft.window_start}
              onChange={(event) => {
                edit({ window_start: event.target.value })
              }}
            >
              {TIMES.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </select>
            <span className="ar-muted">and</span>
            <select
              className="ar-select"
              style={{ flex: 1 }}
              aria-label="End of the send window"
              disabled={disabled}
              value={draft.window_end}
              onChange={(event) => {
                edit({ window_end: event.target.value })
              }}
            >
              {TIMES.map((time) => (
                <option key={time} value={time}>
                  {time}
                </option>
              ))}
            </select>
          </div>
        )}
      </Field>

      <label className="ar-switch">
        <input
          type="checkbox"
          disabled={disabled}
          checked={draft.skip_weekends}
          onChange={(event) => {
            edit({ skip_weekends: event.target.checked })
          }}
        />
        <span className="ar-switch__track" aria-hidden="true" />
        {/* The bare label, as drawn. What the window governs — searches and
            scheduled posts alike — is said on the packs screen and the publish
            screen, beside the things it delays. */}
        <span>Skip weekends</span>
      </label>

      <InlineError failure={failure} />

      <div className="ar-row">
        <span className="ar-card__spacer" />
        <Button
          type="submit"
          tone="primary"
          disabled={disabled || !ordered}
          busy={busy}
          busyLabel="Saving"
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
